import {
  createIntegrationConnection,
  disconnectIntegrationConnection,
  reconnectIntegrationConnection
} from '../../common/utils/integrations-client/jira/connections';
import { getIntegrationToken } from '../../common/api-client';
import {
  type AuthenticatedConnectUser,
  paragon as integrationsClient
} from '@useparagon/connect';
import { type SupportedIntegrationProductSlugs } from '../../common/constants';
import { type getActionsFn } from './getJiraOnPremActions';
import { getDeleteConnection } from './getDeleteConnection';

const { PARAGON_PROJECT_ID } = window.appConfig;

export const getJiraParagonActions: getActionsFn = ({
  displayName,
  integrationProductInfo,
  connection,
  authUser,
  enterprise,
  analytics,
  setIsProcessing,
  setError,
  connectionsUpdated,
  setContents,
  t
}) => {
  type ParagonActionFunction = {
    action(): Promise<void>;
    handleSuccess(): Promise<void>;
    handleFailure(): Promise<void>;
    errorMessage: string;
    setProcessingFlag: boolean;
  };

  const paragonActionWrapper = async (
    action: ParagonActionFunction
  ): Promise<void> => {
    try {
      if (action.setProcessingFlag) {
        setIsProcessing(true);
      }

      const { token: integrationToken } = await getIntegrationToken(
        authUser.token,
        enterprise.id
      );

      await integrationsClient.authenticate(
        PARAGON_PROJECT_ID || '',
        integrationToken
      );
      await action.action();
      await action.handleSuccess();

      connectionsUpdated();
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);

      setError(action.errorMessage);

      await action.handleFailure();
    } finally {
      setIsProcessing(false);
    }
  };

  const connect = async (): Promise<void> => {
    await paragonActionWrapper({
      action: async () => {
        if (!integrationProductInfo.paragonIntegrationName) {
          return;
        }
        // Get connections as of right now
        const beforeConnectUserInfo =
          integrationsClient.getUser() as AuthenticatedConnectUser;
        let beforeConnectCredentials =
          beforeConnectUserInfo.integrations[
            integrationProductInfo.paragonIntegrationName
          ]?.allCredentials;

        // Make a copy of the array of initial credentials, since Paragon replaces the content with newer values after connecting
        if (beforeConnectCredentials) {
          beforeConnectCredentials = [...beforeConnectCredentials];
        }

        // Establish a new connections (this may never return if user cancels connection)
        await integrationsClient.installIntegration(
          integrationProductInfo.paragonIntegrationName,
          {
            allowMultipleCredentials: true
          }
        );

        // Get connections now
        const afterConnectUserInfo =
          integrationsClient.getUser() as AuthenticatedConnectUser;
        const afterConnectCredentials =
          afterConnectUserInfo.integrations[
            integrationProductInfo.paragonIntegrationName
          ]?.allCredentials;

        // Find connections that was added
        if (
          beforeConnectCredentials &&
          afterConnectCredentials &&
          afterConnectCredentials.length > 0
        ) {
          const addedIntegrationCredentials = afterConnectCredentials.filter(
            afterConnectCredential => {
              const thisConnectionWasInOriginalSet =
                beforeConnectCredentials &&
                beforeConnectCredentials.some(beforeConnectCredential => {
                  return (
                    afterConnectCredential.id === beforeConnectCredential.id
                  );
                });

              return !thisConnectionWasInOriginalSet;
            }
          );

          if (addedIntegrationCredentials.length < 1) {
            throw new Error(t('Unable to find added integration'));
          }

          if (addedIntegrationCredentials.length > 1) {
            // if there are more then 1 new connection, log but continue picking one of the connection as the one that was added
            // eslint-disable-next-line no-console
            console.error(
              `Found ${addedIntegrationCredentials.length} new integrations while expecting only one.`
            );
          }

          const addedIntegrationCredential = addedIntegrationCredentials[0];
          const baseUrl = addedIntegrationCredential.providerData
            .instanceUrl as string;

          if (!baseUrl) {
            // eslint-disable-next-line no-console
            console.error('No base URL', addedIntegrationCredential);

            throw new Error(t('Unexpected error while getting base URL'));
          } else if (addedIntegrationCredential.status !== 'VALID') {
            // eslint-disable-next-line no-console
            console.error(
              `Invalid connection status of ${addedIntegrationCredential.status}`,
              addedIntegrationCredential
            );

            throw new Error(
              t('Unexpected connection status {{ connectionStatus }}', {
                connectionStatus: addedIntegrationCredential.status
              })
            );
          } else {
            if (connection) {
              // We have an existing connection on the server side, update it
              await reconnectIntegrationConnection({
                enterpriseId: enterprise.id,
                token: authUser.token,
                connectionId: connection.id,
                body: {
                  credential_id: addedIntegrationCredential.id,
                  base_url: baseUrl
                }
              });
            } else {
              // This is a brand new connection, add it on the server side
              await createIntegrationConnection({
                enterpriseId: enterprise.id,
                token: authUser.token,
                body: {
                  product_slug:
                    integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs,
                  credential_id: addedIntegrationCredential.id,
                  base_url: baseUrl
                }
              });
            }
          }
        } else {
          throw new Error(
            t('Unexpected error while connecting to an integration')
          );
        }
      },
      handleSuccess: async () => {
        setContents(
          t('Successfully connected to {{integrationType}}', {
            integrationType: displayName
          }),
          'confirmation'
        );

        await analytics.integrationConnect({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });
      },
      handleFailure: async () => {
        await analytics.integrationConnectFail({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });
      },
      errorMessage: t(
        'There was an error connecting to {{ displayName }}. Please try again.',
        {
          displayName
        }
      ),
      // We will not keep track of a state when we are connecting (which is used to disable buttons).
      // This is because Paragon might never return from a method to connect (specifically this happens when user cancels the connection)
      setProcessingFlag: false
    });
  };

  const disconnect = async (): Promise<void> => {
    await paragonActionWrapper({
      action: async () => {
        if (
          connection &&
          connection.credential_id &&
          integrationProductInfo.paragonIntegrationName
        ) {
          // Disconnect
          await integrationsClient.uninstallIntegration(
            integrationProductInfo.paragonIntegrationName,
            {
              selectedCredentialId: connection.credential_id
            }
          );

          // Update server side connection
          await disconnectIntegrationConnection({
            enterpriseId: enterprise.id,
            token: authUser.token,
            connectionId: connection.id
          });
        }
      },
      handleSuccess: async () => {
        setContents(
          t('Successfully disconnected from {{integrationType}}', {
            integrationType: displayName
          }),
          'confirmation'
        );

        await analytics.integrationDisconnect({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });
      },
      handleFailure: async () => {
        await analytics.integrationDisconnectFail({
          integration:
            integrationProductInfo.integrationProductSlug as unknown as SupportedIntegrationProductSlugs
        });
      },
      errorMessage: t(
        'There was an error disconnecting from {{ displayName }}. Please try again.',
        {
          displayName
        }
      ),
      setProcessingFlag: true
    });
  };

  const deleteConnection = getDeleteConnection({
    displayName,
    connection,
    authUser,
    enterprise,
    setIsProcessing,
    setError,
    connectionsUpdated,
    setContents,
    t
  }).deleteConnection;

  return {
    connect,
    disconnect,
    deleteConnection
  };
};
